import { z } from 'zod';

const REQUIRED_MESSAGE = 'This field is required';

export const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .email('Please enter a valid email')
    .max(255, 'Maximum 255 characters'),
  password: z.string().min(1, REQUIRED_MESSAGE).max(255, 'Maximum 255 characters'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export const checkEmailSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .email('Please enter a valid email')
    .max(255, 'Maximum 255 characters'),
});

export type CheckEmailFormData = z.infer<typeof checkEmailSchema>;

const PASSWORD_RULES = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 255,
  PATTERN_UPPERCASE: /[A-Z]/,
  PATTERN_NUMBER: /\d/,
  PATTERN_SPECIAL: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/,
} as const;

const PASSWORD_ERROR_MESSAGES = {
  MIN_LENGTH: `Password must be at least ${PASSWORD_RULES.MIN_LENGTH} characters`,
  MAX_LENGTH: `Maximum ${PASSWORD_RULES.MAX_LENGTH} characters`,
  UPPERCASE: 'Must include at least one uppercase letter',
  NUMBER: 'Must include at least one number',
  SPECIAL: 'Must include at least one special character',
} as const;

const passwordField = z
  .string()
  .min(PASSWORD_RULES.MIN_LENGTH, PASSWORD_ERROR_MESSAGES.MIN_LENGTH)
  .max(PASSWORD_RULES.MAX_LENGTH, PASSWORD_ERROR_MESSAGES.MAX_LENGTH)
  .refine((v) => PASSWORD_RULES.PATTERN_UPPERCASE.test(v), PASSWORD_ERROR_MESSAGES.UPPERCASE)
  .refine((v) => PASSWORD_RULES.PATTERN_NUMBER.test(v), PASSWORD_ERROR_MESSAGES.NUMBER)
  .refine((v) => PASSWORD_RULES.PATTERN_SPECIAL.test(v), PASSWORD_ERROR_MESSAGES.SPECIAL);

export const setPasswordSchema = z
  .object({
    password: passwordField,
    confirmPassword: z.string().min(1, REQUIRED_MESSAGE),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

export const addUserSchema = z.object({
  username: z.string().trim().min(1, REQUIRED_MESSAGE).max(100, 'Maximum 100 characters'),
  email: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .email('Please enter a valid email')
    .max(255, 'Maximum 255 characters'),
  name: z.string().trim().min(1, REQUIRED_MESSAGE).max(255, 'Maximum 255 characters'),
  countryId: z.string().optional(),
  role: z.enum(['admin', 'editor'], {
    error: REQUIRED_MESSAGE,
  }),
});

export type AddUserFormData = z.infer<typeof addUserSchema>;

export const editUserSchema = z.object({
  name: z.string().trim().min(1, REQUIRED_MESSAGE).max(255, 'Maximum 255 characters'),
  email: z
    .string()
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .email('Please enter a valid email')
    .max(255, 'Maximum 255 characters'),
  role: z.enum(['admin', 'editor'], {
    error: REQUIRED_MESSAGE,
  }),
  isActive: z.boolean(),
  countryId: z.string().optional(),
});

export type EditUserFormData = z.infer<typeof editUserSchema>;

export const resetPasswordSchema = z
  .object({
    password: passwordField,
    confirmPassword: z.string().min(1, REQUIRED_MESSAGE),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const changeOwnPasswordSchema = z
  .object({
    currentPassword: z.string().min(1, REQUIRED_MESSAGE),
    newPassword: passwordField,
    confirmPassword: z.string().min(1, REQUIRED_MESSAGE),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: 'The new password must be different from the current one',
    path: ['newPassword'],
  });

export type ChangeOwnPasswordFormData = z.infer<typeof changeOwnPasswordSchema>;

export const editionSchema = z.object({
  name: z.string().trim().min(1, REQUIRED_MESSAGE).max(255, 'Maximum 255 characters'),
  year: z
    .number({ error: 'Please select a year' })
    .int()
    .min(1900, 'Year must be between 1900 and 2100')
    .max(2100, 'Year must be between 1900 and 2100'),
  countryId: z.string().min(1, 'Please select a country'),
  /**
   * The PMS site that owns the edition. Empty means a marketplace (SSO) edition,
   * which the backend resolves to the country's SSO site.
   */
  siteId: z.string().optional(),
  externalId: z.number().int().min(1, 'Must be a positive integer').optional(),
});

export type EditionFormData = z.infer<typeof editionSchema>;

export const siteSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2, 'Minimum 2 characters')
    .max(64, 'Maximum 64 characters')
    .regex(
      /^[a-z0-9-]+$/,
      'Lowercase letters, digits and hyphens only. The voting site resolves a hostname to this code.',
    ),
  name: z.string().trim().min(1, REQUIRED_MESSAGE).max(120, 'Maximum 120 characters'),
  countryId: z.string().min(1, 'Please select a country'),
  authMode: z.enum(['sso', 'password']),
  pmsType: z.string().trim().max(32, 'Maximum 32 characters').optional().or(z.literal('')),
  // The access-link export composes voter links from this, so a typo here mails a
  // dead link to a whole country. Validated as an absolute http(s) origin.
  votingBaseUrl: z
    .string()
    .trim()
    .max(255, 'Maximum 255 characters')
    .refine(
      (value) => value === '' || /^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(value),
      'Must be a full URL, e.g. https://awards.tuotempo.es',
    )
    .optional()
    .or(z.literal('')),
});

export type SiteFormData = z.infer<typeof siteSchema>;

export const categoryNameSchema = z.object({
  name: z.string().trim().min(1, REQUIRED_MESSAGE).max(255, 'Maximum 255 characters'),
});

export type CategoryNameFormData = z.infer<typeof categoryNameSchema>;

export const editNomineeSchema = z.object({
  name: z.string().trim().min(1, REQUIRED_MESSAGE).max(255, 'Maximum 255 characters'),
  surname: z.string().trim().max(255, 'Maximum 255 characters').optional(),
  photoUrl: z
    .string()
    .url('Please enter a valid URL')
    .max(500, 'Maximum 500 characters')
    .or(z.literal(''))
    .optional(),
  profileUrl: z
    .string()
    .url('Please enter a valid URL')
    .max(500, 'Maximum 500 characters')
    .or(z.literal(''))
    .optional(),
  city: z.string().max(255, 'Maximum 255 characters').optional(),
});

export type EditNomineeFormData = z.infer<typeof editNomineeSchema>;
